import { useState, useEffect } from "react";
import { ContactUsProps } from "../types/public";
import Container from "../components/basicComponents/container";
import TextInput from "../components/basicComponents/text-input";
import * as Icons from "../assets/SVGs/index";
import ImageUpload from "../components/basicComponents/image-upload";
import Button from "../components/basicComponents/button";
import renderFormSection from "../utils/form-section";
import { getInfo, addInfo, editInfo } from "../state-management/actions/site-info-actions";
import { useDispatch } from "react-redux";
import TextArea from "../components/basicComponents/text-area";
import SVGUpload from "../components/basicComponents/logo-upload"; // Ensure correct import path

const initialData: ContactUsProps = {
  name: "",
  descriptions: [{ title: "", value: "" }],
  our_mission: "",
  short_description: "",
  picture: "",
  logo: "",
  emails: [{ title: "", value: "" }],
  phones: [{ title: "", value: "" }],
  addresses: [{ title: "", value: "" }],
  zipcode: "",
};

const handleImageChange = (
  file: File | null,
  setFormData: React.Dispatch<React.SetStateAction<any>>,
  key: string,
  storageKey: string
) => {
  if (file) {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setFormData((prev: any) => ({
        ...prev,
        [key]: base64String,
      }));
      localStorage.setItem(storageKey, base64String);
    };
    reader.readAsDataURL(file);
  } else {
    setFormData((prev: any) => ({
      ...prev,
      [key]: "",
    }));
    localStorage.removeItem(storageKey);
  }
};

export default function ContactsUs() {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<any>(initialData);
  const [editFlag, setEditFlag] = useState(false);
  const [id, setId] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      const response = await getInfo()(dispatch);
      if (response.data.length > 0) {
        const { _id, ...cleanData } = response.data[0];
        setId(_id);
        setFormData(cleanData);
        setEditFlag(true);
      } else {
        setEditFlag(false);
      }
    };

    fetchData();
  }, []);

  const handleChangeFormData = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    try {
      if (!editFlag) {
        await addInfo(formData)(dispatch);
      } else {
        await editInfo(id, formData)(dispatch);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      <Container header="تماس با ما">
        <div className="w-full">
          <TextInput
            name="name"
            value={formData.name}
            placeholder="عنوان صفحه"
            onChange={handleChangeFormData}
            RightIcon={<Icons.TitleIcon className="w-5 h-5" />}
          />
          <SVGUpload
            onSVGChange={(file) => handleImageChange(file, setFormData, "logo", "siteLogo")}
            storageKey="siteLogo"
            label="لوگو"
            defaultSVG={formData.logo} // Default logo from state
            uploadButtonText="آپلود لوگو"
          />
          <ImageUpload
            onImageChange={(file) =>
              handleImageChange(file, setFormData, "picture", "siteImage")
            }
            storageKey="siteImage"
            label="تصویر"
            defaultImage={formData.picture}
          />
        </div>

        {/* Phones */}
        <div className="mb-6 md:mt-2 mt-24 w-full">
          {renderFormSection({
            dataKey: "phones",
            formData,
            setFormData,
            fields: [
              {
                name: "title",
                placeholder: "نوع تلفن",
                RightIcon: <Icons.PhoneIcon className="w-5 h-5" />,
              },
              {
                name: "value",
                placeholder: "شماره تلفن",
                RightIcon: <Icons.PhoneIcon className="w-5 h-5" />,
              },
            ],
            addItemLabel: "افزودن شماره جدید",
          })}
        </div>

        {/* Emails */}
        <div className="w-full">
          {renderFormSection({
            dataKey: "emails",
            formData,
            setFormData,
            fields: [
              {
                name: "title",
                placeholder: "نوع ایمیل",
                RightIcon: <Icons.MailIcon className="w-5 h-5" />,
              },
              {
                name: "value",
                placeholder: "ایمیل",
                RightIcon: <Icons.MailIcon className="w-5 h-5" />,
              },
            ],
            addItemLabel: "افزودن ایمیل جدید",
          })}
        </div>

        {/* Addresses */}
        <div className="mb-6 w-full">
          {renderFormSection({
            dataKey: "addresses",
            formData,
            setFormData,
            fields: [
              {
                name: "title",
                placeholder: "نوع آدرس",
                RightIcon: <Icons.LocationIcon className="w-5 h-5" />,
              },
              {
                name: "value",
                placeholder: "آدرس",
                RightIcon: <Icons.LocationIcon className="w-5 h-5" />,
              },
            ],
            addItemLabel: "افزودن آدرس جدید",
          })}
        </div>
        <div className="mb-6 w-full">
          {/* descriptions */}
          {renderFormSection({
            dataKey: "descriptions",
            formData,
            setFormData,
            fields: [
              {
                name: "title",
                placeholder: "عنوان",
                RightIcon: <Icons.CalenderIcon className="w-5 h-5 absolute" />,
              },
              { name: "value", placeholder: "توضیحات", component: "TextArea" },
            ],
            addItemLabel: "افزودن عنوان جدید",
          })}
        </div>
        {/* About Us */}
        <div className="flex flex-col gap-y-4 w-full"
          style={{ margin: "30px 0 10px 0" }}>
          <p>درباره ما</p>
          <TextArea
            name="short_description"
            value={formData.short_description}
            placeholder="درباره ما"
            onChange={handleChangeFormData}
          />
        </div>

        {/* Our Mission */}
        <div className="flex flex-col gap-y-4 w-full"
          style={{ margin: "30px 0 10px 0" }}>
          <p>رسالت ما</p>
          <TextArea
            name="our_mission"
            value={formData.our_mission}
            placeholder="رسالت ما"
            onChange={handleChangeFormData}
          />
        </div>

        {/* Zipcode */}
        <div className="flex flex-col gap-y-4 w-full"
          style={{ margin: "30px 0 10px 0" }}>
          <TextInput
            name="zipcode"
            value={formData.zipcode}
            placeholder="کد پستی"
            onChange={handleChangeFormData}
            RightIcon={<Icons.TitleIcon className="w-5 h-5" />}
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            variant="success"
            title="ثبت"
            paddingx="20px"
            paddingy="6px"
            onClick={handleSubmit}
          />
        </div>
      </Container>
    </div>
  );
}
